import { and, asc, desc, eq, gte, inArray, isNull, lte, sql } from 'drizzle-orm';
import { format } from 'date-fns';
import { db, runInTransaction } from '@/db/client';
import {
    accounts,
    categories,
    people,
    personalLoanEvents,
    personalLoans,
    subcategories,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { resolveTransactionTime } from '@/lib/transactionTime';
import { PersonalLoanEntryType, type PersonalLoanEntryTypeValue } from '@/lib/constants';
import { getMonthDateRange } from '@/lib/months';
import { recalculateAccountBalance } from './accountRepository';
import { setPersonalLoanTags, syncPersonalLoanTransactionTags } from './tagRepository';
import { excludeIgnored } from './transactionScopes';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';

export type Person = typeof people.$inferSelect;
export type PersonalLoan = typeof personalLoans.$inferSelect;
export type PersonalLoanEvent = typeof personalLoanEvents.$inferSelect;
export type PersonalLoanStatus = 'open' | 'settled' | 'written_off';
export type PersonalLoanEventType = 'receipt' | 'write_off' | 'reschedule';

export interface PersonalLoanWithRelations extends PersonalLoan {
    personName?: string | null;
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
}

export interface PersonalLoanEventWithRelations extends PersonalLoanEvent {
    transactionDescription?: string | null;
}

/** Uma linha do Cronograma de recebimento: data + valor esperado (ver ADR 0036). */
export interface ScheduleInstallmentInput {
    date: string;
    amount: number;
}

/** Recebimento esperado em aberto, materializado como transação pendente. */
export interface ScheduledReceiptInstallment {
    id: string;
    date: string;
    amount: number;
}

export interface PersonReceivableSummary {
    personId: string;
    personName: string;
    totalOutstanding: number;
    loanCount: number;
    /** Data combinada mais próxima entre os empréstimos em aberto da pessoa. */
    nextAgreedDate: string | null;
}

export interface WhoOwesMeSummary {
    totalOutstanding: number;
    people: PersonReceivableSummary[];
}

type NewPersonalLoanInput = {
    personId?: string | null;
    personName?: string | null;
    /** Conta da saída. Obrigatória, exceto em empréstimo já existente. */
    accountId?: string | null;
    amount: number;
    date: string;
    agreedDate?: string | null;
    /**
     * Cronograma de recebimento (ADR 0036): N recebimentos esperados que devem
     * somar o valor emprestado. Vazio/ausente cai no caso da `agreedDate` (um só)
     * ou "quando der" (nenhum).
     */
    schedule?: ScheduleInstallmentInput[] | null;
    notes?: string | null;
    /** Empréstimo já existente: nasce com saldo em aberto, sem transação de saída. */
    isExisting?: boolean;
    /** Tags do empréstimo — aplicadas a todos os lançamentos que ele gera. */
    tagIds?: string[];
};

type RecordReceiptInput = {
    loanId: string;
    date: string;
    /** Valor total recebido — o que passar do saldo vira excedente (receita). */
    amount: number;
    /** Conta que recebeu. Obrigatória, exceto em Recebimento já existente. */
    accountId?: string | null;
    /**
     * Recebimento já existente (ADR 0058): aconteceu antes do app, o dinheiro já
     * está no saldo inicial das contas. Abate o saldo do empréstimo sem criar
     * transação nem tocar conta. Só em empréstimo já existente; não admite excedente.
     */
    isExistingReceipt?: boolean;
    /** Categoria de receita do excedente — obrigatória quando há excedente. */
    excessCategoryId?: string | null;
    excessSubcategoryId?: string | null;
    notes?: string | null;
    /**
     * Id da transação do principal, quando quem chama precisa referenciá-la depois
     * (o abate de notificação vincula a pendente ao lançamento que criou). Omitido,
     * é gerado aqui como sempre.
     */
    transactionId?: string;
};

type RecordWriteOffInput = {
    loanId: string;
    date: string;
    /** Valor baixado — omitido = baixa total do saldo em aberto. */
    amount?: number | null;
    categoryId: string;
    subcategoryId?: string | null;
    notes?: string | null;
};

type UpdatePersonalLoanInput = {
    loanId: string;
    amount?: number | null;
    date?: string | null;
    accountId?: string | null;
    notes?: string | null;
    /** Novo conjunto de tags do empréstimo — ausente = não mexer; [] = limpar. */
    tagIds?: string[];
};

function normalizeOptionalText(value?: string | null): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

function roundCurrency(value: number): number {
    return Math.round(value * 100) / 100;
}

// Tolerância de centavo pra comparar somas de cronograma sem tropeçar em float.
const CURRENCY_EPSILON = 0.005;

/** Descrição canônica da entrada esperada — mesma string em qualquer parcela. */
function scheduledReceiptDescription(personName?: string | null): string {
    return i18n.t('personalLoans:generated.scheduledReceipt', { name: loanPersonName(personName) });
}

/** Nome exibível da pessoa nas descrições geradas (dado criado no idioma corrente). */
function loanPersonName(personName?: string | null): string {
    return personName ?? i18n.t('personalLoans:generated.personFallback');
}

/**
 * Valida e normaliza um Cronograma de recebimento (ADR 0036): datas presentes,
 * valores > 0 e soma == alvo (o saldo/principal que o cronograma quebra no tempo).
 * Lista vazia = "quando der". Ordena por data. Lança em entrada inválida.
 */
function normalizeScheduleInstallments(
    installments: ScheduleInstallmentInput[],
    target: number,
): ScheduleInstallmentInput[] {
    const cleaned = installments
        .map((it) => ({ date: normalizeOptionalText(it.date), amount: roundCurrency(it.amount) }))
        .filter((it): it is ScheduleInstallmentInput => it.date !== null);
    if (cleaned.length === 0) return [];
    if (cleaned.some((it) => !(it.amount > 0))) {
        throw new ExpectedError(i18n.t('ui:errors.scheduleEntryMustBePositive'));
    }
    const sum = roundCurrency(cleaned.reduce((total, it) => total + it.amount, 0));
    if (Math.abs(sum - roundCurrency(target)) > CURRENCY_EPSILON) {
        throw new ExpectedError(i18n.t('ui:errors.scheduleMustMatchOutstanding'));
    }
    return [...cleaned].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Serializa o cronograma PRETENDIDO (fonte durável do plano) — só faz sentido com 2+ parcelas. */
function serializeIntendedSchedule(installments: ScheduleInstallmentInput[]): string {
    return JSON.stringify(installments.map((it) => ({ date: it.date, amount: roundCurrency(it.amount) })));
}

/** Lê o cronograma pretendido de personalLoans.schedule; null se ausente/vazio/inválido. */
function parseIntendedSchedule(raw: string | null | undefined): ScheduleInstallmentInput[] | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        const cleaned = parsed
            .filter((it): it is ScheduleInstallmentInput =>
                !!it && typeof it.date === 'string' && typeof it.amount === 'number' && it.amount > 0)
            .map((it) => ({ date: it.date, amount: roundCurrency(it.amount) }))
            .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
        return cleaned.length > 0 ? cleaned : null;
    } catch {
        return null;
    }
}

/**
 * Deriva a cauda EM ABERTO a partir do cronograma pretendido e do quanto já entrou: consome
 * as parcelas mais antigas em cascata FIFO (a mais antiga inteira, a seguinte encolhida). É
 * função PURA de (pretendido, recebido) — por isso desfazer um recebimento (o `received` cai)
 * devolve exatamente as parcelas antigas, sem depender do estado lossy das linhas abertas.
 * Exportada para teste. Ver ADR 0036.
 */
export function reduceScheduleByReceived(
    intended: ScheduleInstallmentInput[],
    received: number,
): ScheduleInstallmentInput[] {
    let remaining = roundCurrency(Math.max(received, 0));
    const tail: ScheduleInstallmentInput[] = [];
    for (const installment of intended) {
        if (remaining <= CURRENCY_EPSILON) {
            tail.push(installment);
            continue;
        }
        if (installment.amount - remaining <= CURRENCY_EPSILON) {
            // Parcela inteiramente coberta pelo já-recebido → sai da cauda.
            remaining = roundCurrency(remaining - installment.amount);
            continue;
        }
        // Parcela parcialmente coberta → encolhe pro que resta.
        tail.push({ date: installment.date, amount: roundCurrency(installment.amount - remaining) });
        remaining = 0;
    }
    return tail;
}

/**
 * Normaliza nome para comparação de duplicidade: o lower() do SQLite só dobra
 * ASCII, então 'João'/'Joao'/'JOÃO' escapariam. Aqui tiramos acento e caixa em JS.
 */
function normalizeName(value: string): string {
    return value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * Monta o objeto de inser\u00e7\u00e3o de uma transa\u00e7\u00e3o de empr\u00e9stimo com todos os campos
 * neutros j\u00e1 preenchidos \u2014 uma \u00fanica fonte de verdade para o shape, em vez de
 * repetir ~30 campos em cada lan\u00e7amento (sa\u00edda, recebimento, excedente, pendente).
 */
function buildLoanTransaction(input: {
    id?: string;
    type: 'income' | 'expense';
    amount: number;
    description: string;
    date: string;
    accountId: string | null;
    personalLoanId: string;
    personalLoanEntryType: PersonalLoanEntryTypeValue;
    categoryId?: string | null;
    subcategoryId?: string | null;
    notes?: string | null;
    isConfirmed: number;
    now: string;
}) {
    return {
        id: input.id ?? generateId(),
        type: input.type,
        amount: input.amount,
        description: input.description,
        date: input.date,
        // Efetivado (saída, recebimento, excedente, baixa) carimba a hora quando é HOJE;
        // entrada ESPERADA (isConfirmed 0) fica sem — é agendamento, não acontecimento,
        // e ganha a hora quando o recebimento for registrado de verdade.
        time: input.isConfirmed ? resolveTransactionTime(input.date) : null,
        notes: input.notes ?? null,
        accountId: input.accountId,
        creditCardId: null,
        invoiceId: null,
        transferDestinationAccountId: null,
        transferId: null,
        categoryId: input.categoryId ?? null,
        subcategoryId: input.subcategoryId ?? null,
        isRecurring: 0,
        recurringId: null,
        installmentId: null,
        installmentNumber: null,
        installmentTotal: null,
        financingId: null,
        financingEntryType: null,
        financingInstallmentNumber: null,
        personalLoanId: input.personalLoanId,
        personalLoanEntryType: input.personalLoanEntryType,
        adjustmentType: null,
        isConfirmed: input.isConfirmed,
        isHiddenFromSubs: 0,
        createdAt: input.now,
        updatedAt: input.now,
        deletedAt: null,
    };
}

// ─── Pessoa (devedor) ────────────────────────────────────────

export async function getPeople(): Promise<Person[]> {
    return db.select().from(people).orderBy(asc(people.name));
}

export async function getOrCreatePerson(name: string): Promise<Person> {
    const trimmed = name.trim();
    if (!trimmed) {
        throw new ExpectedError(i18n.t('ui:errors.personNameRequired'));
    }

    const normalized = normalizeName(trimmed);
    const allPeople = await db.select().from(people);
    const existing = allPeople.find((p) => normalizeName(p.name) === normalized);
    if (existing) return existing;

    const now = new Date().toISOString();
    const id = generateId();
    await db.insert(people).values({
        id,
        name: trimmed,
        createdAt: now,
        updatedAt: now,
    });

    const created = await db.select().from(people).where(eq(people.id, id));
    return created[0];
}

/** Renomeia a pessoa e propaga o novo nome para os lançamentos vinculados. */
export async function renamePerson(personId: string, name: string): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
        throw new ExpectedError(i18n.t('ui:errors.personNameRequired'));
    }

    const normalized = normalizeName(trimmed);
    const allPeople = await db.select({ id: people.id, name: people.name }).from(people);
    const duplicate = allPeople.find((p) => normalizeName(p.name) === normalized && p.id !== personId);
    if (duplicate) {
        throw new ExpectedError(i18n.t('ui:errors.personNameTaken'));
    }

    const now = new Date().toISOString();
    await db
        .update(people)
        .set({ name: trimmed, updatedAt: now })
        .where(eq(people.id, personId));

    const loanRows = await db
        .select({ id: personalLoans.id })
        .from(personalLoans)
        .where(eq(personalLoans.personId, personId));
    const loanIds = loanRows.map((row) => row.id);
    if (loanIds.length === 0) return;

    // Uma passada só: o CASE resolve a descrição por entry type (evita 5 UPDATEs
    // sequenciais sobre a mesma tabela).
    await db
        .update(transactions)
        .set({
            description: sql`CASE ${transactions.personalLoanEntryType}
                WHEN ${PersonalLoanEntryType.DISBURSEMENT} THEN ${i18n.t('personalLoans:generated.loanTo', { name: trimmed })}
                WHEN ${PersonalLoanEntryType.SCHEDULED_RECEIPT} THEN ${i18n.t('personalLoans:generated.scheduledReceipt', { name: trimmed })}
                WHEN ${PersonalLoanEntryType.RECEIPT} THEN ${i18n.t('personalLoans:generated.receiptFrom', { name: trimmed })}
                WHEN ${PersonalLoanEntryType.EXCESS} THEN ${i18n.t('personalLoans:generated.excessFrom', { name: trimmed })}
                WHEN ${PersonalLoanEntryType.WRITE_OFF} THEN ${i18n.t('personalLoans:generated.writeOff', { name: trimmed })}
                ELSE ${transactions.description} END`,
            updatedAt: now,
        })
        .where(and(
            inArray(transactions.personalLoanId, loanIds),
            isNull(transactions.deletedAt),
        ));
}

// ─── Leitura ─────────────────────────────────────────────────

async function getLoanBaseRows(whereClause?: any): Promise<PersonalLoanWithRelations[]> {
    const query = db
        .select({
            id: personalLoans.id,
            personId: personalLoans.personId,
            accountId: personalLoans.accountId,
            amount: personalLoans.amount,
            outstandingBalance: personalLoans.outstandingBalance,
            date: personalLoans.date,
            agreedDate: personalLoans.agreedDate,
            schedule: personalLoans.schedule,
            status: personalLoans.status,
            notes: personalLoans.notes,
            isExisting: personalLoans.isExisting,
            createdAt: personalLoans.createdAt,
            updatedAt: personalLoans.updatedAt,
            personName: people.name,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
        })
        .from(personalLoans)
        .leftJoin(people, eq(personalLoans.personId, people.id))
        .leftJoin(accounts, eq(personalLoans.accountId, accounts.id));

    return (whereClause ? query.where(whereClause) : query)
        .orderBy(
            asc(personalLoans.status),
            sql`${personalLoans.agreedDate} IS NULL`,
            asc(personalLoans.agreedDate),
            desc(personalLoans.date),
        );
}

export async function getPersonalLoans(
    statuses: PersonalLoanStatus[] = ['open'],
): Promise<PersonalLoanWithRelations[]> {
    const whereClause = statuses.length > 0
        ? inArray(personalLoans.status, statuses)
        : undefined;
    return getLoanBaseRows(whereClause);
}

export async function getPersonalLoanById(id: string): Promise<PersonalLoanWithRelations | null> {
    const rows = await getLoanBaseRows(eq(personalLoans.id, id));
    return rows[0] ?? null;
}

export async function getPersonalLoanEvents(
    loanId: string,
): Promise<PersonalLoanEventWithRelations[]> {
    return db
        .select({
            id: personalLoanEvents.id,
            loanId: personalLoanEvents.loanId,
            type: personalLoanEvents.type,
            date: personalLoanEvents.date,
            amount: personalLoanEvents.amount,
            excessAmount: personalLoanEvents.excessAmount,
            newOutstandingBalance: personalLoanEvents.newOutstandingBalance,
            agreedDate: personalLoanEvents.agreedDate,
            notes: personalLoanEvents.notes,
            transactionId: personalLoanEvents.transactionId,
            excessTransactionId: personalLoanEvents.excessTransactionId,
            createdAt: personalLoanEvents.createdAt,
            transactionDescription: transactions.description,
        })
        .from(personalLoanEvents)
        .leftJoin(transactions, eq(personalLoanEvents.transactionId, transactions.id))
        .where(eq(personalLoanEvents.loanId, loanId))
        .orderBy(desc(personalLoanEvents.date), desc(personalLoanEvents.createdAt));
}

/** Agregado "Quem me deve": total a receber e saldo por pessoa (empréstimos em aberto). */
export async function getWhoOwesMeSummary(): Promise<WhoOwesMeSummary> {
    const rows = await db
        .select({
            personId: personalLoans.personId,
            personName: people.name,
            totalOutstanding: sql<number>`COALESCE(SUM(${personalLoans.outstandingBalance}), 0)`.as('totalOutstanding'),
            loanCount: sql<number>`COUNT(*)`.as('loanCount'),
            nextAgreedDate: sql<string | null>`MIN(${personalLoans.agreedDate})`.as('nextAgreedDate'),
        })
        .from(personalLoans)
        .innerJoin(people, eq(personalLoans.personId, people.id))
        .where(eq(personalLoans.status, 'open'))
        .groupBy(personalLoans.personId)
        .orderBy(sql`totalOutstanding DESC`);

    const summaries: PersonReceivableSummary[] = rows.map((row) => ({
        personId: row.personId,
        personName: row.personName ?? 'Pessoa',
        totalOutstanding: roundCurrency(row.totalOutstanding),
        loanCount: row.loanCount,
        nextAgreedDate: row.nextAgreedDate ?? null,
    }));

    return {
        totalOutstanding: roundCurrency(summaries.reduce((sum, row) => sum + row.totalOutstanding, 0)),
        people: summaries,
    };
}

/**
 * Quanto dos **recebimentos esperados** cai dentro do mês — o pedaço do "quem me deve"
 * que as Pendências daquele mês JÁ contam.
 *
 * Existe porque os dois números convivem na mesma tela: a seção da home mostra o
 * ESTOQUE (tudo que te devem hoje) e "Pendências › A receber" mostra o FLUXO do mês.
 * Empréstimo com data combinada materializa um `scheduled_receipt` na data
 * (`syncScheduledReceipt`) e entra no fluxo; o "quando der" não materializa nada, e o
 * atrasado tem data em mês passado — nenhum dos dois aparece no mês aberto. Sem este
 * número a home mostraria o mesmo dinheiro duas vezes sem dizer que é o mesmo.
 *
 * Repete os filtros de `getPendingCashflowSummary` (não confirmado, dentro do mês,
 * ignorado de fora): se divergirem, o rodapé da seção passa a contradizer as Pendências
 * logo acima dele.
 */
export async function getScheduledReceiptsTotalInMonth(month: string, monthStartDay?: number): Promise<number> {
    const { startDate, endDate } = getMonthDateRange(month, monthStartDay);

    const [row] = await db
        .select({
            total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
        })
        .from(transactions)
        .where(and(
            isNull(transactions.deletedAt),
            eq(transactions.isConfirmed, 0),
            eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            gte(transactions.date, startDate),
            lte(transactions.date, endDate),
            excludeIgnored(),
        ));

    return roundCurrency(row?.total ?? 0);
}

// ─── Estado derivado ─────────────────────────────────────────

/**
 * Recalcula o saldo em aberto e o status a partir dos eventos (o razão) e
 * sincroniza o lançamento pendente da data combinada. Espelha syncFinancingState:
 * qualquer operação que mexa em evento/empréstimo termina chamando isto.
 */
export async function syncPersonalLoanState(loanId: string): Promise<void> {
    const [loan] = await db
        .select()
        .from(personalLoans)
        .where(eq(personalLoans.id, loanId));
    if (!loan) return;

    const events = await db
        .select()
        .from(personalLoanEvents)
        .where(eq(personalLoanEvents.loanId, loanId))
        .orderBy(asc(personalLoanEvents.date), asc(personalLoanEvents.createdAt));

    // Reprocessa o razão em ordem cronológica: recalcula o saldo vivo, ressincroniza
    // o snapshot "Saldo após" de cada evento (que de outra forma ficaria stale ao
    // desfazer um recebimento) e descobre o tipo do evento que efetivamente zerou o
    // saldo — status terminal robusto a baixa/recebimento com data retroativa.
    const now = new Date().toISOString();
    let runningBalance = loan.amount;
    let closingType: 'receipt' | 'write_off' | null = null;
    for (const event of events) {
        if (event.type !== 'reschedule') {
            runningBalance = roundCurrency(Math.max(runningBalance - event.amount, 0));
            if (closingType === null && runningBalance <= 0) {
                closingType = event.type as 'receipt' | 'write_off';
            }
        }
        const snapshot = roundCurrency(runningBalance);
        if (event.newOutstandingBalance !== snapshot) {
            await db
                .update(personalLoanEvents)
                .set({ newOutstandingBalance: snapshot })
                .where(eq(personalLoanEvents.id, event.id));
        }
    }

    const outstandingBalance = roundCurrency(runningBalance);
    const status: PersonalLoanStatus = outstandingBalance > 0
        ? 'open'
        : closingType === 'write_off' ? 'written_off' : 'settled';

    await db
        .update(personalLoans)
        .set({ outstandingBalance, status, updatedAt: now })
        .where(eq(personalLoans.id, loanId));

    await syncScheduledReceipt({ ...loan, outstandingBalance, status });
    await syncPersonalLoanTransactionTags(loanId);
}

/**
 * Reescreve o Cronograma de recebimento por inteiro: some com os pendentes abertos
 * e materializa a lista dada como N `scheduled_receipt` (income pendente). É o único
 * ponto que grava a cauda — a renegociação e a criação com cronograma passam por aqui.
 */
async function materializeSchedule(
    loanId: string,
    accountId: string | null,
    installments: ScheduleInstallmentInput[],
    personName: string | null | undefined,
    now: string,
): Promise<void> {
    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(and(
            eq(transactions.personalLoanId, loanId),
            eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            isNull(transactions.deletedAt),
        ));

    const description = scheduledReceiptDescription(personName);
    for (const installment of installments) {
        await db.insert(transactions).values(buildLoanTransaction({
            type: 'income',
            amount: installment.amount,
            description,
            date: installment.date,
            accountId,
            personalLoanId: loanId,
            personalLoanEntryType: PersonalLoanEntryType.SCHEDULED_RECEIPT,
            isConfirmed: 0,
            now,
        }));
    }
}

/**
 * Faz as linhas `scheduled_receipt` abertas baterem com a cauda-ALVO (derivada do cronograma
 * pretendido). Casa por DATA: preserva o id de uma parcela que só encolheu, RE-INSERE as que o
 * desfazer trouxe de volta e apaga as que saíram. Baixo churn/oplog — só escreve o que muda.
 */
async function reconcileScheduledReceiptsToTarget(
    loan: PersonalLoan,
    target: ScheduleInstallmentInput[],
    openReceipts: typeof transactions.$inferSelect[],
    description: string,
    now: string,
): Promise<void> {
    const remaining = [...openReceipts];
    for (const installment of target) {
        const index = remaining.findIndex((row) => row.date === installment.date);
        if (index >= 0) {
            const [row] = remaining.splice(index, 1);
            const patch: Partial<typeof transactions.$inferInsert> = {};
            if (roundCurrency(row.amount) !== installment.amount) patch.amount = installment.amount;
            if (row.description !== description) patch.description = description;
            if (row.accountId !== loan.accountId) patch.accountId = loan.accountId;
            if (Object.keys(patch).length > 0) {
                await db
                    .update(transactions)
                    .set({ ...patch, updatedAt: now })
                    .where(eq(transactions.id, row.id));
            }
        } else {
            await db.insert(transactions).values(buildLoanTransaction({
                type: 'income',
                amount: installment.amount,
                description,
                date: installment.date,
                accountId: loan.accountId,
                personalLoanId: loan.id,
                personalLoanEntryType: PersonalLoanEntryType.SCHEDULED_RECEIPT,
                isConfirmed: 0,
                now,
            }));
        }
    }
    for (const row of remaining) {
        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(eq(transactions.id, row.id));
    }
}

/**
 * Reconcilia os recebimentos esperados pendentes ao saldo em aberto (ADR 0036). O
 * Cronograma são N `scheduled_receipt` cujo total == saldo; é o que coloca o
 * empréstimo no fluxo de caixa projetado. Regras:
 *  - Encerrado ou saldo zerado → some com todos os pendentes.
 *  - Sem cronograma materializado mas com data combinada → materializa UM (o saldo
 *    todo na data), preservando o caso degenerado "data única" e empréstimos legados.
 *  - Cronograma existente → reconcilia o total ao saldo em cascata FIFO: o que entrou
 *    (ou baixou) consome os recebimentos mais antigos; um saldo restaurado (undo)
 *    volta ao mais antigo. A `agreedDate` vira a data do recebimento mais antigo em
 *    aberto — é dela que a lista/detalhe derivam "Atrasado".
 */
async function syncScheduledReceipt(loan: PersonalLoan): Promise<void> {
    const now = new Date().toISOString();
    const openReceipts = await db
        .select()
        .from(transactions)
        .where(and(
            eq(transactions.personalLoanId, loan.id),
            eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            isNull(transactions.deletedAt),
        ))
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    const shouldExist = loan.status === 'open' && loan.outstandingBalance > 0;

    // Encerrado/quitado: some com a cauda. A `agreedDate` é preservada de propósito —
    // desfazer o recebimento que quitou reabre e o ramo de bootstrap remonta a entrada.
    if (!shouldExist) {
        if (openReceipts.length > 0) {
            await db
                .update(transactions)
                .set({ deletedAt: now, updatedAt: now })
                .where(and(
                    eq(transactions.personalLoanId, loan.id),
                    eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
                    isNull(transactions.deletedAt),
                ));
        }
        return;
    }

    // Cronograma pretendido (2+ parcelas, ADR 0036): a cauda viva é DERIVADA dele —
    // cauda = pretendido menos o que já entrou (FIFO). Como é função pura de (pretendido,
    // saldo), desfazer um recebimento restaura as parcelas antigas e a âncora não regride:
    // o plano do usuário é preservado. Reconstrói a cauda inclusive quando ela sumiu (undo
    // de um recebimento que havia quitado). Data única / "quando der" / legado (sem plano
    // guardado) seguem nos ramos abaixo, sobre as próprias linhas abertas.
    const intended = parseIntendedSchedule(loan.schedule);
    if (intended && intended.length >= 2) {
        const [person] = await db.select().from(people).where(eq(people.id, loan.personId));
        const description = scheduledReceiptDescription(person?.name);
        const intendedSum = roundCurrency(intended.reduce((sum, it) => sum + it.amount, 0));
        const received = roundCurrency(Math.max(intendedSum - loan.outstandingBalance, 0));
        const derived = reduceScheduleByReceived(intended, received);
        // Defensivo: arredondamento não deve esvaziar a cauda com saldo vivo.
        const target = derived.length > 0
            ? derived
            : [{ date: intended[0].date, amount: loan.outstandingBalance }];
        await reconcileScheduledReceiptsToTarget(loan, target, openReceipts, description, now);
        const anchorDate = target[0].date;
        if (loan.agreedDate !== anchorDate) {
            await db
                .update(personalLoans)
                .set({ agreedDate: anchorDate, updatedAt: now })
                .where(eq(personalLoans.id, loan.id));
        }
        return;
    }

    // Bootstrap do caso degenerado "data única": sem cronograma materializado, mas com
    // data combinada, materializa um recebimento esperado do saldo inteiro.
    if (openReceipts.length === 0) {
        if (loan.agreedDate) {
            const [person] = await db.select().from(people).where(eq(people.id, loan.personId));
            await db.insert(transactions).values(buildLoanTransaction({
                type: 'income',
                amount: loan.outstandingBalance,
                description: scheduledReceiptDescription(person?.name),
                date: loan.agreedDate,
                accountId: loan.accountId,
                personalLoanId: loan.id,
                personalLoanEntryType: PersonalLoanEntryType.SCHEDULED_RECEIPT,
                isConfirmed: 0,
                now,
            }));
        }
        return;
    }

    // Reconcilia o cronograma existente ao saldo.
    const [person] = await db.select().from(people).where(eq(people.id, loan.personId));
    const description = scheduledReceiptDescription(person?.name);
    const currentSum = roundCurrency(openReceipts.reduce((sum, receipt) => sum + receipt.amount, 0));

    let survivors = openReceipts.map((receipt) => ({
        id: receipt.id,
        date: receipt.date,
        amount: receipt.amount,
    }));

    if (currentSum - loan.outstandingBalance > CURRENCY_EPSILON) {
        // Entrou dinheiro (ou houve baixa): consome o excedente da cauda mais antiga.
        let excess = roundCurrency(currentSum - loan.outstandingBalance);
        const kept: typeof survivors = [];
        for (const receipt of survivors) {
            if (excess <= CURRENCY_EPSILON) {
                kept.push(receipt);
                continue;
            }
            if (receipt.amount - excess <= CURRENCY_EPSILON) {
                // Recebimento inteiramente coberto → some.
                await db
                    .update(transactions)
                    .set({ deletedAt: now, updatedAt: now })
                    .where(eq(transactions.id, receipt.id));
                excess = roundCurrency(excess - receipt.amount);
            } else {
                // Recebimento parcialmente coberto → encolhe pro que resta.
                kept.push({ ...receipt, amount: roundCurrency(receipt.amount - excess) });
                excess = 0;
            }
        }
        survivors = kept;
    } else if (loan.outstandingBalance - currentSum > CURRENCY_EPSILON && survivors.length > 0) {
        // Saldo restaurado (undo de recebimento/baixa): devolve ao mais antigo.
        const missing = roundCurrency(loan.outstandingBalance - currentSum);
        survivors[0] = { ...survivors[0], amount: roundCurrency(survivors[0].amount + missing) };
    }

    // Defensivo: se a matemática de arredondamento esvaziou a cauda com saldo vivo,
    // remonta um recebimento único pra não sumir do fluxo de caixa projetado.
    if (survivors.length === 0) {
        await db.insert(transactions).values(buildLoanTransaction({
            type: 'income',
            amount: loan.outstandingBalance,
            description,
            date: loan.agreedDate ?? format(new Date(), 'yyyy-MM-dd'),
            accountId: loan.accountId,
            personalLoanId: loan.id,
            personalLoanEntryType: PersonalLoanEntryType.SCHEDULED_RECEIPT,
            isConfirmed: 0,
            now,
        }));
    } else {
        // Persiste valor/descrição/conta que tenham derivado (recebimento, renome, troca de conta).
        for (const receipt of survivors) {
            const original = openReceipts.find((row) => row.id === receipt.id)!;
            const patch: Partial<typeof transactions.$inferInsert> = {};
            if (original.amount !== receipt.amount) patch.amount = receipt.amount;
            if (original.description !== description) patch.description = description;
            if (original.accountId !== loan.accountId) patch.accountId = loan.accountId;
            if (Object.keys(patch).length > 0) {
                await db
                    .update(transactions)
                    .set({ ...patch, updatedAt: now })
                    .where(eq(transactions.id, receipt.id));
            }
        }
    }

    // A data combinada segue o recebimento mais antigo em aberto.
    const anchorDate = survivors.length > 0
        ? survivors.reduce((min, receipt) => (receipt.date < min ? receipt.date : min), survivors[0].date)
        : loan.agreedDate;
    if (loan.agreedDate !== anchorDate) {
        await db
            .update(personalLoans)
            .set({ agreedDate: anchorDate, updatedAt: now })
            .where(eq(personalLoans.id, loan.id));
    }
}

// ─── Criação ─────────────────────────────────────────────────

export async function createPersonalLoan(data: NewPersonalLoanInput): Promise<PersonalLoanWithRelations> {
    if (!data.amount || data.amount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.loanAmountInvalid'));
    }
    if (!data.date) {
        throw new ExpectedError(i18n.t('ui:errors.loanDateRequired'));
    }

    const isExisting = data.isExisting === true;
    const accountId = normalizeOptionalText(data.accountId);

    if (!isExisting && !accountId) {
        throw new ExpectedError(i18n.t('ui:errors.sourceAccountRequired'));
    }

    if (accountId) {
        const accountRows = await db
            .select({ id: accounts.id })
            .from(accounts)
            .where(and(eq(accounts.id, accountId), isNull(accounts.deletedAt)));
        if (accountRows.length === 0) {
            throw new ExpectedError(i18n.t('ui:errors.accountGone'));
        }
    }

    let person: Person;
    if (data.personId) {
        const rows = await db.select().from(people).where(eq(people.id, data.personId));
        if (!rows[0]) {
            throw new ExpectedError(i18n.t('ui:errors.personNotFound'));
        }
        person = rows[0];
    } else {
        person = await getOrCreatePerson(data.personName ?? '');
    }

    // Cronograma explícito (N recebimentos, soma == valor) tem prioridade sobre a
    // data única; sem nenhum dos dois é "quando der". A `agreedDate` guardada é a
    // âncora — a data do primeiro recebimento esperado.
    const schedule = data.schedule && data.schedule.length > 0
        ? normalizeScheduleInstallments(data.schedule, data.amount)
        : [];
    const agreedDate = schedule.length > 0
        ? schedule[0].date
        : normalizeOptionalText(data.agreedDate);

    const loanId = generateId();
    const now = new Date().toISOString();

    await db.insert(personalLoans).values({
        id: loanId,
        personId: person.id,
        accountId,
        amount: data.amount,
        outstandingBalance: data.amount,
        date: data.date,
        agreedDate,
        // Só 2+ parcelas guardam o plano durável; data única/"quando der" ficam no
        // agreedDate/cauda (o sync trata como caso degenerado).
        schedule: schedule.length >= 2 ? serializeIntendedSchedule(schedule) : null,
        status: 'open',
        notes: normalizeOptionalText(data.notes),
        isExisting: isExisting ? 1 : 0,
        createdAt: now,
        updatedAt: now,
    });

    if (!isExisting) {
        await db.insert(transactions).values(buildLoanTransaction({
            type: 'expense',
            amount: data.amount,
            description: i18n.t('personalLoans:generated.loanTo', { name: person.name }),
            date: data.date,
            accountId,
            personalLoanId: loanId,
            personalLoanEntryType: PersonalLoanEntryType.DISBURSEMENT,
            isConfirmed: 1,
            now,
        }));

        await recalculateAccountBalance(accountId!);
    }

    // Cronograma de 2+ parcelas é materializado direto; o caso de uma parcela (só
    // `agreedDate`) o sync abaixo cria como bootstrap.
    if (schedule.length > 0) {
        await materializeSchedule(loanId, accountId, schedule, person.name, now);
    }

    await syncPersonalLoanState(loanId);

    // Depois do sync: no empréstimo "já existente" sem cronograma a primeira
    // linha (bootstrap da data combinada) só nasce dentro do sync.
    if (data.tagIds && data.tagIds.length > 0) {
        await setPersonalLoanTags(loanId, data.tagIds);
    }

    return (await getPersonalLoanById(loanId))!;
}

// ─── Recebimento ─────────────────────────────────────────────

export async function recordPersonalLoanReceipt(data: RecordReceiptInput): Promise<PersonalLoanWithRelations> {
    const loan = await getPersonalLoanById(data.loanId);
    if (!loan) {
        throw new Error('Empréstimo nao encontrado.');
    }
    if (loan.status !== 'open') {
        throw new ExpectedError(i18n.t('ui:errors.loanAlreadyClosed'));
    }
    if (!data.amount || data.amount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.paymentAmountInvalid'));
    }
    if (!data.date) {
        throw new ExpectedError(i18n.t('ui:errors.paymentDateRequired'));
    }

    const isExistingReceipt = data.isExistingReceipt === true;
    let accountId: string | null = null;
    if (isExistingReceipt) {
        // Espelho do empréstimo já existente: o dinheiro entrou antes do app e já
        // está no saldo inicial — registrar com conta contaria duas vezes.
        if (!loan.isExisting) {
            throw new ExpectedError(i18n.t('ui:errors.existingReceiptOnlyOnExistingLoan'));
        }
        if (roundCurrency(data.amount) - loan.outstandingBalance > CURRENCY_EPSILON) {
            throw new ExpectedError(i18n.t('ui:errors.existingReceiptExceedsOutstanding'));
        }
    } else {
        accountId = normalizeOptionalText(data.accountId);
        if (!accountId) {
            throw new ExpectedError(i18n.t('ui:errors.receivingAccountRequired'));
        }
        const accountRows = await db
            .select({ id: accounts.id })
            .from(accounts)
            .where(and(eq(accounts.id, accountId), isNull(accounts.deletedAt)));
        if (accountRows.length === 0) {
            throw new ExpectedError(i18n.t('ui:errors.accountGone'));
        }
    }

    // O saldo nunca fica negativo: o que passar do saldo é excedente — e
    // excedente é receita de verdade, com categoria.
    const principal = roundCurrency(Math.min(data.amount, loan.outstandingBalance));
    const excess = roundCurrency(data.amount - principal);

    let excessCategoryId: string | null = null;
    let excessSubcategoryId: string | null = null;
    if (excess > 0) {
        excessCategoryId = normalizeOptionalText(data.excessCategoryId);
        excessSubcategoryId = normalizeOptionalText(data.excessSubcategoryId);
        if (!excessCategoryId) {
            throw new ExpectedError(i18n.t('ui:errors.chooseExcessIncomeCategory'));
        }
        const categoryRows = await db
            .select({ id: categories.id, type: categories.type })
            .from(categories)
            .where(and(eq(categories.id, excessCategoryId), isNull(categories.deletedAt)));
        if (categoryRows.length === 0 || categoryRows[0].type !== 'income') {
            throw new ExpectedError(i18n.t('ui:errors.excessCategoryMustBeIncome'));
        }
        if (excessSubcategoryId) {
            const subcategoryRows = await db
                .select({ id: subcategories.id, categoryId: subcategories.categoryId })
                .from(subcategories)
                .where(and(eq(subcategories.id, excessSubcategoryId), isNull(subcategories.deletedAt)));
            if (!subcategoryRows[0] || subcategoryRows[0].categoryId !== excessCategoryId) {
                excessSubcategoryId = null;
            }
        }
    }

    const now = new Date().toISOString();
    const notes = normalizeOptionalText(data.notes);

    // Recebimento já existente não tem transação — só o evento no razão.
    const transactionId = isExistingReceipt
        ? null
        : normalizeOptionalText(data.transactionId) ?? generateId();
    // Receita(s) + EVENTO nascem juntos: o saldo do empréstimo deriva dos eventos
    // (syncPersonalLoanState) — crash entre a receita e o evento deixava o dinheiro
    // contado na conta com o recebível ainda em aberto (dupla contagem) e a transação
    // indeletável pela UI (deleteTransaction bloqueia personalLoanId).
    await runInTransaction(async () => {
        if (transactionId) {
            await db.insert(transactions).values(buildLoanTransaction({
                id: transactionId,
                type: 'income',
                amount: principal,
                description: i18n.t('personalLoans:generated.receiptFrom', { name: loanPersonName(loan.personName) }),
                date: data.date,
                notes,
                accountId,
                personalLoanId: loan.id,
                personalLoanEntryType: PersonalLoanEntryType.RECEIPT,
                isConfirmed: 1,
                now,
            }));
        }

        let excessTransactionId: string | null = null;
        if (excess > 0) {
            excessTransactionId = generateId();
            await db.insert(transactions).values(buildLoanTransaction({
                id: excessTransactionId,
                type: 'income',
                amount: excess,
                description: i18n.t('personalLoans:generated.excessFrom', { name: loanPersonName(loan.personName) }),
                date: data.date,
                notes,
                accountId,
                categoryId: excessCategoryId,
                subcategoryId: excessSubcategoryId,
                personalLoanId: loan.id,
                personalLoanEntryType: PersonalLoanEntryType.EXCESS,
                isConfirmed: 1,
                now,
            }));
        }

        await db.insert(personalLoanEvents).values({
            id: generateId(),
            loanId: loan.id,
            type: 'receipt',
            date: data.date,
            amount: principal,
            excessAmount: excess > 0 ? excess : null,
            newOutstandingBalance: roundCurrency(loan.outstandingBalance - principal),
            agreedDate: null,
            notes,
            transactionId,
            excessTransactionId,
            createdAt: now,
        });

        if (accountId) {
            await recalculateAccountBalance(accountId);
        }
        await syncPersonalLoanState(loan.id);
    });

    return (await getPersonalLoanById(loan.id))!;
}

// ─── Baixa ───────────────────────────────────────────────────

export async function recordPersonalLoanWriteOff(data: RecordWriteOffInput): Promise<PersonalLoanWithRelations> {
    const loan = await getPersonalLoanById(data.loanId);
    if (!loan) {
        throw new Error('Empréstimo nao encontrado.');
    }
    if (loan.status !== 'open') {
        throw new ExpectedError(i18n.t('ui:errors.loanAlreadyClosed'));
    }
    if (!data.date) {
        throw new ExpectedError(i18n.t('ui:errors.writeOffDateRequired'));
    }

    const amount = roundCurrency(data.amount ?? loan.outstandingBalance);
    if (amount <= 0) {
        throw new ExpectedError(i18n.t('ui:errors.writeOffAmountInvalid'));
    }
    if (amount > loan.outstandingBalance) {
        throw new ExpectedError(i18n.t('ui:errors.writeOffExceedsOutstanding'));
    }

    const categoryId = normalizeOptionalText(data.categoryId);
    if (!categoryId) {
        throw new ExpectedError(i18n.t('ui:errors.chooseWriteOffExpenseCategory'));
    }
    const categoryRows = await db
        .select({ id: categories.id, type: categories.type })
        .from(categories)
        .where(and(eq(categories.id, categoryId), isNull(categories.deletedAt)));
    if (categoryRows.length === 0 || categoryRows[0].type !== 'expense') {
        throw new ExpectedError(i18n.t('ui:errors.writeOffCategoryMustBeExpense'));
    }
    let subcategoryId = normalizeOptionalText(data.subcategoryId);
    if (subcategoryId) {
        const subcategoryRows = await db
            .select({ id: subcategories.id, categoryId: subcategories.categoryId })
            .from(subcategories)
            .where(and(eq(subcategories.id, subcategoryId), isNull(subcategories.deletedAt)));
        if (!subcategoryRows[0] || subcategoryRows[0].categoryId !== categoryId) {
            subcategoryId = null;
        }
    }

    const now = new Date().toISOString();
    const notes = normalizeOptionalText(data.notes);

    // A baixa vira despesa na data da baixa, mas NÃO mexe em conta: o dinheiro
    // saiu lá atrás, no empréstimo — aqui o recebível só deixa de ser ativo.
    const transactionId = generateId();
    // Mesma razão do recebimento: despesa + evento atômicos ou nada.
    await runInTransaction(async () => {
        await db.insert(transactions).values(buildLoanTransaction({
            id: transactionId,
            type: 'expense',
            amount,
            description: i18n.t('personalLoans:generated.writeOff', { name: loanPersonName(loan.personName) }),
            date: data.date,
            notes,
            accountId: null,
            categoryId,
            subcategoryId,
            personalLoanId: loan.id,
            personalLoanEntryType: PersonalLoanEntryType.WRITE_OFF,
            isConfirmed: 1,
            now,
        }));

        await db.insert(personalLoanEvents).values({
            id: generateId(),
            loanId: loan.id,
            type: 'write_off',
            date: data.date,
            amount,
            excessAmount: null,
            newOutstandingBalance: roundCurrency(loan.outstandingBalance - amount),
            agreedDate: null,
            notes,
            transactionId,
            excessTransactionId: null,
            createdAt: now,
        });

        await syncPersonalLoanState(loan.id);
    });

    return (await getPersonalLoanById(loan.id))!;
}

// ─── Cronograma de recebimento ───────────────────────────────

/** Recebimentos esperados em aberto (o Cronograma vigente), ordenados por data. */
export async function getPersonalLoanSchedule(loanId: string): Promise<ScheduledReceiptInstallment[]> {
    const rows = await db
        .select({
            id: transactions.id,
            date: transactions.date,
            amount: transactions.amount,
        })
        .from(transactions)
        .where(and(
            eq(transactions.personalLoanId, loanId),
            eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            isNull(transactions.deletedAt),
        ))
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    return rows.map((row) => ({ id: row.id, date: row.date, amount: roundCurrency(row.amount) }));
}

/**
 * Define, renegocia ou remove o Cronograma de recebimento (ADR 0036). A cauda em
 * aberto é reescrita por inteiro (o já-pago é história), somando o saldo vigente:
 *  - `schedule` (N parcelas) → cronograma explícito; soma tem que bater com o saldo.
 *  - só `agreedDate` → caso degenerado de uma parcela (o saldo todo na data).
 *  - nada / lista vazia → "quando der".
 */
export async function reschedulePersonalLoan(data: {
    loanId: string;
    agreedDate?: string | null;
    schedule?: ScheduleInstallmentInput[] | null;
    notes?: string | null;
}): Promise<PersonalLoanWithRelations> {
    const loan = await getPersonalLoanById(data.loanId);
    if (!loan) {
        throw new Error('Empréstimo nao encontrado.');
    }
    if (loan.status !== 'open') {
        throw new ExpectedError(i18n.t('ui:errors.loanAlreadyClosed'));
    }

    const installments = data.schedule !== undefined && data.schedule !== null
        ? normalizeScheduleInstallments(data.schedule, loan.outstandingBalance)
        : (() => {
            const agreedDate = normalizeOptionalText(data.agreedDate);
            return agreedDate ? [{ date: agreedDate, amount: loan.outstandingBalance }] : [];
        })();
    const anchorDate = installments.length > 0 ? installments[0].date : null;

    // Cronograma atual em aberto — detecta a renegociação idêntica pra não poluir o
    // histórico (mas ainda auto-healar a cauda pendente).
    const current = await db
        .select({ date: transactions.date, amount: transactions.amount })
        .from(transactions)
        .where(and(
            eq(transactions.personalLoanId, loan.id),
            eq(transactions.personalLoanEntryType, PersonalLoanEntryType.SCHEDULED_RECEIPT),
            isNull(transactions.deletedAt),
        ))
        .orderBy(asc(transactions.date), asc(transactions.createdAt));

    const unchanged = current.length === installments.length
        && installments.every((installment, index) =>
            current[index].date === installment.date
            && Math.abs(current[index].amount - installment.amount) <= CURRENCY_EPSILON);
    if (unchanged) {
        await syncPersonalLoanState(loan.id);
        return (await getPersonalLoanById(loan.id))!;
    }

    const now = new Date().toISOString();

    await materializeSchedule(loan.id, loan.accountId, installments, loan.personName, now);

    await db.insert(personalLoanEvents).values({
        id: generateId(),
        loanId: loan.id,
        type: 'reschedule',
        date: format(new Date(), 'yyyy-MM-dd'),
        amount: 0,
        excessAmount: null,
        newOutstandingBalance: loan.outstandingBalance,
        agreedDate: anchorDate,
        notes: normalizeOptionalText(data.notes),
        transactionId: null,
        excessTransactionId: null,
        createdAt: now,
    });

    // Grava a âncora antes do sync: pra "quando der" (sem parcelas) é ela, nula, que
    // impede o bootstrap de remontar uma entrada na data antiga. Guarda também o plano
    // pretendido (2+ parcelas) — fonte durável de onde o sync deriva a cauda; data única/
    // "quando der" zeram (null) pra cair no caso degenerado.
    await db
        .update(personalLoans)
        .set({
            agreedDate: anchorDate,
            schedule: installments.length >= 2 ? serializeIntendedSchedule(installments) : null,
            updatedAt: now,
        })
        .where(eq(personalLoans.id, loan.id));

    await syncPersonalLoanState(loan.id);

    return (await getPersonalLoanById(loan.id))!;
}

// ─── Edição ──────────────────────────────────────────────────

/**
 * Edição estrutural do empréstimo. Notas são sempre editáveis; valor, data e
 * conta só num empréstimo "intocado" (sem recebimento nem baixa) — depois disso
 * a história financeira já depende deles.
 */
export async function updatePersonalLoan(data: UpdatePersonalLoanInput): Promise<PersonalLoanWithRelations> {
    const loan = await getPersonalLoanById(data.loanId);
    if (!loan) {
        throw new Error('Empréstimo nao encontrado.');
    }

    const wantsStructuralChange = (
        (data.amount != null && data.amount !== loan.amount)
        || (data.date != null && data.date !== loan.date)
        || (data.accountId !== undefined && normalizeOptionalText(data.accountId) !== loan.accountId)
    );

    if (wantsStructuralChange) {
        const settlingEvents = await db
            .select({ id: personalLoanEvents.id })
            .from(personalLoanEvents)
            .where(and(
                eq(personalLoanEvents.loanId, loan.id),
                inArray(personalLoanEvents.type, ['receipt', 'write_off']),
            ))
            .limit(1);
        if (settlingEvents.length > 0) {
            throw new ExpectedError(i18n.t('ui:errors.loanLockedAfterFirstEntry'));
        }
        if (data.amount != null && data.amount <= 0) {
            throw new ExpectedError(i18n.t('ui:errors.loanAmountInvalid'));
        }
    }

    const nextAccountId = data.accountId !== undefined
        ? normalizeOptionalText(data.accountId)
        : loan.accountId;
    if (!loan.isExisting && !nextAccountId) {
        throw new ExpectedError(i18n.t('ui:errors.sourceAccountRequired'));
    }
    if (nextAccountId && nextAccountId !== loan.accountId) {
        const accountRows = await db
            .select({ id: accounts.id })
            .from(accounts)
            .where(and(eq(accounts.id, nextAccountId), isNull(accounts.deletedAt)));
        if (accountRows.length === 0) {
            throw new ExpectedError(i18n.t('ui:errors.accountGone'));
        }
    }

    const now = new Date().toISOString();
    const nextAmount = data.amount ?? loan.amount;
    const nextDate = data.date ?? loan.date;

    await db
        .update(personalLoans)
        .set({
            amount: nextAmount,
            date: nextDate,
            accountId: nextAccountId,
            ...(data.notes !== undefined ? { notes: normalizeOptionalText(data.notes) } : {}),
            updatedAt: now,
        })
        .where(eq(personalLoans.id, loan.id));

    if (wantsStructuralChange && !loan.isExisting) {
        await db
            .update(transactions)
            .set({
                amount: nextAmount,
                date: nextDate,
                accountId: nextAccountId,
                updatedAt: now,
            })
            .where(and(
                eq(transactions.personalLoanId, loan.id),
                eq(transactions.personalLoanEntryType, PersonalLoanEntryType.DISBURSEMENT),
                isNull(transactions.deletedAt),
            ));

        if (loan.accountId && loan.accountId !== nextAccountId) {
            await recalculateAccountBalance(loan.accountId);
        }
        if (nextAccountId) {
            await recalculateAccountBalance(nextAccountId);
        }
    }

    // Replace antes do sync — o sync só propaga união, não remove tag.
    if (data.tagIds) {
        await setPersonalLoanTags(loan.id, data.tagIds);
    }

    await syncPersonalLoanState(loan.id);

    return (await getPersonalLoanById(loan.id))!;
}

// ─── Exclusões ───────────────────────────────────────────────

/**
 * Desfaz um recebimento ou baixa: soft-deleta os lançamentos vinculados, apaga o
 * evento e recalcula saldo/status a partir dos eventos que sobraram (um empréstimo
 * encerrado pode voltar a aberto). Eventos de renegociação não são desfeitos —
 * são histórico, não movimentação.
 */
export async function deletePersonalLoanEvent(eventId: string): Promise<void> {
    const [event] = await db
        .select()
        .from(personalLoanEvents)
        .where(eq(personalLoanEvents.id, eventId));
    if (!event) {
        throw new Error('Evento nao encontrado.');
    }
    if (event.type === 'reschedule') {
        throw new ExpectedError(i18n.t('ui:errors.renegotiationsAreHistory'));
    }

    const now = new Date().toISOString();
    const affectedAccounts = new Set<string>();

    const linkedIds = [event.transactionId, event.excessTransactionId]
        .filter((id): id is string => !!id);
    if (linkedIds.length > 0) {
        const linkedRows = await db
            .select({ id: transactions.id, accountId: transactions.accountId })
            .from(transactions)
            .where(inArray(transactions.id, linkedIds));
        for (const row of linkedRows) {
            if (row.accountId) affectedAccounts.add(row.accountId);
        }

        await db
            .update(transactions)
            .set({ deletedAt: now, updatedAt: now })
            .where(and(
                inArray(transactions.id, linkedIds),
                isNull(transactions.deletedAt),
            ));
    }

    await db.delete(personalLoanEvents).where(eq(personalLoanEvents.id, eventId));

    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
    await syncPersonalLoanState(event.loanId);
}

/**
 * Exclusão TOTAL do empréstimo: soft-delete de todos os lançamentos vinculados
 * (o efeito da saída e dos recebimentos é desfeito no saldo das contas), remove
 * os eventos e o registro do empréstimo (a tabela não tem deletedAt).
 */
export async function deletePersonalLoan(loanId: string): Promise<void> {
    const loan = await getPersonalLoanById(loanId);
    if (!loan) return;

    const now = new Date().toISOString();

    const linkedRows = await db
        .select({ id: transactions.id, accountId: transactions.accountId })
        .from(transactions)
        .where(eq(transactions.personalLoanId, loanId));

    await db
        .update(transactions)
        .set({ deletedAt: now, updatedAt: now })
        .where(and(
            eq(transactions.personalLoanId, loanId),
            isNull(transactions.deletedAt),
        ));

    // Desvincula TODAS as transações (inclusive as já soft-deletadas):
    // transactions.personalLoanId referencia personal_loans(id) e o hard delete
    // abaixo violaria a foreign key se algum vínculo sobrasse.
    await db
        .update(transactions)
        .set({ personalLoanId: null, personalLoanEntryType: null })
        .where(eq(transactions.personalLoanId, loanId));

    await db.delete(personalLoanEvents).where(eq(personalLoanEvents.loanId, loanId));
    await db.delete(personalLoans).where(eq(personalLoans.id, loanId));

    const affectedAccounts = new Set<string>();
    for (const row of linkedRows) {
        if (row.accountId) affectedAccounts.add(row.accountId);
    }
    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
}
